import {createFileRoute, notFound} from "@tanstack/react-router";
import {z as zod} from "zod";
import {IST_TIME_ZONE_ID} from "~/utilities/timeZones";
import {fetchDatasets} from "~/utilities/reports/reportsAnalyticsApi";
import type {DatasetMetadata} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {ADGPT__DATASETS__QUERY_KEY, REPORT__DETAILS__QUERY_KEY} from "~/utilities/queryKeys";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {
    reportZodValidator,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/Report";
import {hydrateReportForFrontend, type HydratedReport} from "~/utilities/reports/hydrateReportForFrontend";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug",
)({
    loader: async ({context, params}) => {
        const reportIdResult = uuidZodValidator.safeParse(params.reportSlug);
        if (reportIdResult.success === false) {
            throw notFound();
        }
        const reportId = reportIdResult.data;

        const report = await context.queryClient.ensureQueryData<HydratedReport | null>({
            queryKey: [REPORT__DETAILS__QUERY_KEY, reportId],
            queryFn: async () => {
                const reportResult = await robustJsonResultClientFetch(
                    `${context.intellsysBaseUrl}/sub-system/pluto/reports/get-by-id`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                            "X-Workspace-Id": context.workspace.id,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            reportId,
                        }),
                    },
                    // TODO: Why is this giving error ??
                    reportZodValidator.nullable(),
                );
                if (reportResult.success === false) {
                    throw reportResult.error;
                }
                const report = reportResult.data;

                return report === null ? null : hydrateReportForFrontend(report);
            },
            retry: false,
            staleTime: Infinity,
        });
        if (report === null) {
            throw notFound();
        }

        const isFavouriteResult = await robustJsonResultClientFetch(
            `${context.intellsysBaseUrl}/sub-system/pluto/reports/get-is-favourite`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${context.authenticationStatus.accessToken}`,
                    "X-Workspace-Id": context.workspace.id,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    reportId,
                }),
            },
            zod.object({
                isFavourite: zod.boolean(),
            }),
        );
        if (isFavouriteResult.success === false) {
            throw isFavouriteResult.error;
        }
        const isFavourite = isFavouriteResult.data.isFavourite;

        const datasets = await context.queryClient.ensureQueryData<Array<DatasetMetadata>>({
            queryKey: [ADGPT__DATASETS__QUERY_KEY, context.workspace.id],
            queryFn: async () => {
                const result = await fetchDatasets({
                    intellsysBaseUrl: context.intellsysBaseUrl,
                    accessToken: context.authenticationStatus.accessToken,
                    workspaceId: context.workspace.id,
                });
                if (result.success === false) {
                    throw result.error;
                }
                return result.data;
            },
            retry: false,
            staleTime: Infinity,
        });

        return {
            report,
            isFavourite,
            datasets,
            workspace: context.workspace,
            accessToken: context.authenticationStatus.accessToken,
            intellsysBaseUrl: context.intellsysBaseUrl,
            userAccessLevel: context.userAccessLevelOnWorkspace,
            timezone: context.workspace.timeZoneId === IST_TIME_ZONE_ID ? "Asia/Kolkata" : "UTC",
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
